import { useEffect, useRef } from "react";
import useApi from "./useApi";
import { saveWorkflowPifDisplayNames, WorkflowPifDisplayNamesResult } from "../services/workflowService";
import { WorkflowPifDisplayNamesRequestDto } from "../types/workflowDto";

interface PifDisplayNamesOptions {
  instanceId: number | null;
  /** The case's tag as loaded: a fresh load is a fresh attempt. */
  rowVersionTag: string | null;
  /** pif-3 is submitted, its names are still missing, and this is the case's creator. */
  enabled: boolean;
  /** Reloads the case once the server has written the names. */
  onSaved: () => void;
}

/**
 * Asks the server to write pif-3's display names from the IDs the case saved (Pooja, 2026-10-08):
 * right after pif-3's submit (the reload that follows is the first load past pif-3) and again on any
 * later load while they are missing. One attempt per load of the case, never a loop.
 */
const useWorkflowPifDisplayNames = ({ instanceId, rowVersionTag, enabled, onSaved }: PifDisplayNamesOptions) => {
  const { executeApi } = useApi();
  const attemptedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !instanceId) return;
    const attempt = `${instanceId}#${rowVersionTag ?? ""}`;
    if (attemptedRef.current === attempt) return;
    attemptedRef.current = attempt;
    void executeApi<WorkflowPifDisplayNamesRequestDto, WorkflowPifDisplayNamesResult>(saveWorkflowPifDisplayNames, {
      instanceId,
    }).then((result) => {
      if (result?.kind === "saved") onSaved();
    });
  }, [enabled, instanceId, rowVersionTag, executeApi, onSaved]);
};

export default useWorkflowPifDisplayNames;
